import { useEffect, useState, useRef } from 'react';
import confetti from 'canvas-confetti';

/**
 * Écran de score "vivant" : anneau circulaire animé + compteur qui monte +
 * message d'encouragement dynamique (varié, jamais punitif) + salve de confettis
 * sur un bon score. Réutilisé sur l'écran de résultat connecté et open/campagne.
 */
const BANDS = [
    { min: 90, msgs: ['Excellent ! 🏆', 'Brillant ! 🌟', 'Maîtrise totale ! 🎯'] },
    { min: 70, msgs: ['Bien joué 👏', 'Solide ! 💪', 'Beau score ✨'] },
    { min: 50, msgs: ['Tu progresses 💪', 'En bonne voie 🚀', 'Ça vient ! 👍'] },
    { min: 0,  msgs: ['On retravaille ça ensemble 🌱', 'Chaque erreur te fait progresser 💡', 'Accroche-toi, tu vas y arriver 🌱'] },
];

function pickMessage(pct) {
    const band = BANDS.find(b => pct >= b.min) || BANDS[BANDS.length - 1];
    return band.msgs[Math.floor(Math.random() * band.msgs.length)];
}

export default function ScoreCelebration({ pct = 0, correct = null, total = null, color = '#7c3aed', size = 160 }) {
    const [display, setDisplay] = useState(0);
    const [message] = useState(() => pickMessage(pct));
    const firedRef = useRef(false);

    // Compteur animé 0 → pct (easeOutCubic)
    useEffect(() => {
        let raf;
        const start = performance.now();
        const dur = 900;
        const tick = (now) => {
            const t = Math.min(1, (now - start) / dur);
            const eased = 1 - Math.pow(1 - t, 3);
            setDisplay(Math.round(eased * pct));
            if (t < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [pct]);

    // Confettis (une seule fois) sur un bon score — au-dessus des overlays (zIndex haut).
    useEffect(() => {
        if (firedRef.current) return;
        firedRef.current = true;
        if (pct >= 70) {
            const t = setTimeout(() => {
                confetti({
                    particleCount: pct >= 90 ? 170 : 90,
                    spread: 78,
                    startVelocity: 45,
                    origin: { y: 0.35 },
                    scalar: 0.9,
                    zIndex: 10001,
                    disableForReducedMotion: true,
                });
            }, 450);
            return () => clearTimeout(t);
        }
    }, [pct]);

    const stroke = 11;
    const r = (size - stroke) / 2;
    const circ = 2 * Math.PI * r;
    const offset = circ * (1 - display / 100);
    const ringColor = pct >= 70 ? color : pct >= 50 ? '#f59e0b' : '#ef4444';

    return (
        <div className="score-celebration" style={{ textAlign: 'center' }}>
            <style>{`
                .score-celebration { animation: scIn 0.4s ease both; }
                @keyframes scIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
                .score-celebration__msg { animation: scPop 0.5s 0.35s ease both; }
                @keyframes scPop { 0% { transform: scale(0.8); opacity: 0; } 60% { transform: scale(1.06); } 100% { transform: scale(1); opacity: 1; } }
                @media (prefers-reduced-motion: reduce) {
                    .score-celebration, .score-celebration__msg { animation: none; }
                }
            `}</style>
            <div style={{ position: 'relative', width: size, height: size, margin: '0 auto' }}>
                <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
                    <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--border, #e5e7eb)" strokeWidth={stroke} />
                    <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={ringColor} strokeWidth={stroke}
                        strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset}
                        style={{ transition: 'stroke 0.3s' }} />
                </svg>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ fontSize: size * 0.26, fontWeight: 800, lineHeight: 1, color: ringColor, fontFamily: 'var(--font-display)' }}>{display}%</div>
                    {(correct != null && total != null) && (
                        <div style={{ fontSize: '0.82em', color: 'var(--text-muted)', marginTop: 4 }}>{correct} / {total}</div>
                    )}
                </div>
            </div>
            <div className="score-celebration__msg" style={{ fontSize: '1.2em', fontWeight: 700, marginTop: 14, color: 'var(--heading, inherit)' }}>{message}</div>
        </div>
    );
}
